import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { StorageService } from '../services/storage.service';
import { AuthService } from '../services/auth.service';
import { LoginService } from '../services/login.service';
import { DecryptedUserPermissions } from '../models/user-permissions.model';

/**
 * Allows /patient-mfa during pending login MFA, or profile-enable while logged in.
 * PE-6129 — profile-enable requires tenant modulesetting mfa enabled.
 */
export const mfaPendingGuard: CanActivateFn = (route): boolean | UrlTree => {
  const router = inject(Router);
  const storageService = inject(StorageService);
  const authService = inject(AuthService);
  const loginService = inject(LoginService);
  const context = route.queryParamMap.get('context');

  if (context === 'profile-enable') {
    const userInfo = storageService.getSessionStorage('user_info');
    const tenantCode = storageService.getSessionStorage('tenant_code');
    const permissions = storageService.getSessionStorage('user_permissions') as
      DecryptedUserPermissions | null;
    // PE-6129 — block profile MFA when tenant modulesetting mfa is off/missing
    if (!loginService.isTenantMfaModuleEnabled(permissions)) {
      return router.createUrlTree(['/account-settings']);
    }
    if (userInfo?.sessionId && authService.getMfaSubjectId() && tenantCode) {
      return true;
    }
    return router.createUrlTree(['/account-settings']);
  }

  const pending = storageService.getSessionStorage('pending_mfa_login');
  if (pending?.userInfo?.sessionId && pending?.npatientid) {
    return true;
  }

  const shortName = sessionStorage.getItem('sign_in_return_short_name')?.trim()
    || sessionStorage.getItem('tenant_short_name')?.trim();
  if (shortName) {
    authService.clearPortalSession();
    return router.createUrlTree([`/${shortName}`], { queryParams: { sessionEnded: '1' } });
  }

  const signInTree = authService.createSignInUrlTree();
  if (signInTree) {
    return signInTree;
  }
  return router.createUrlTree(['/page-not-found']);
};
